import { useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import {
  useListAccounts,
  useDeleteAccount,
  type Account,
  type ListAccountsKind,
  type ListAccountsStatus,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { gstTreatmentLabel } from "@/lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ListPagination } from "@/components/common/list-pagination";
import { AddAccountDialog } from "@/components/accounts/add-account-dialog";
import { CreateAccountDialog } from "@/components/accounts/create-account-dialog";

// Phase 23-07 + 2026-06-10 redesign slice 1:
//  • Add Lead / Add Account CTAs (top-right). Add Lead is a shortcut to
//    the existing /leads/new flow.
//  • "Hide Test Data" filter (default ON) — strips legalName LIKE 'Test %'
//    rows client-side so the operator's normal view isn't drowned in
//    seed data.
//  • Per-row actions: Edit (pencil) + Archive (status flip via
//    useUpdateAccount). No hard delete — accounts only soft-archive.
//  • Column rework: GSTIN dropped (low utility / takes width), Created
//    date added.
export default function AccountsPage() {
  const [, navigate] = useLocation();
  // Phase 54 (SPACE-UX-ACCT-FILT): optional ?spaceId= deep-link, e.g. from the
  // hub Accounts tab (Plan 54-06). Invisible query param — no visible chip/
  // dropdown surfacing it in v1 (per CONTEXT.md Discretion).
  const urlSearch = useSearch();
  const spaceIdFromQuery = useMemo(() => {
    const raw = new URLSearchParams(urlSearch).get("spaceId");
    if (raw == null) return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [urlSearch]);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | ListAccountsKind>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | ListAccountsStatus>("all");
  const [hideTestData, setHideTestData] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const deleteAccount = useDeleteAccount();

  // "Hide test data" is a CLIENT-SIDE filter. If we let the server paginate
  // first (page/pageSize), it would page BEFORE the filter runs, so a page of
  // 25 with 18 test rows would collapse to 7 and the totals would read "7 of 7"
  // even though 17 real accounts exist. Instead we fetch the full result set
  // (server max 200 — the account count is well under that here) and do BOTH
  // the test-data filter and the pagination client-side.
  const params = {
    q: search || undefined,
    kind: kindFilter === "all" ? undefined : (kindFilter as ListAccountsKind),
    status: statusFilter === "all" ? undefined : (statusFilter as ListAccountsStatus),
    spaceId: spaceIdFromQuery,
    page: 1,
    pageSize: 200,
  };

  const { data: accounts, isLoading } = useListAccounts(params, {
    query: { queryKey: ["/api/accounts", params] },
  });

  const allItems = accounts ?? [];
  const filtered = hideTestData
    ? allItems.filter((a: Account) => !/^Test /i.test(a.legalName))
    : allItems;
  const hiddenTestCount = allItems.length - filtered.length;

  // Client-side pagination over the filtered set.
  const totalFiltered = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const items = filtered.slice(pageStart, pageStart + pageSize);

  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  async function confirmDelete() {
    if (!deletingAccount) return;
    try {
      await deleteAccount.mutateAsync({ id: deletingAccount.id });
      toast({ title: `"${deletingAccount.legalName}" deleted.` });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      setDeletingAccount(null);
    } catch (err) {
      // The server returns 409 { code: "account_has_dependents", blockers: [...] }.
      // That structured body lives on ApiError.data — err.message is only
      // "HTTP 409 Conflict" (buildErrorMessage ignores `code`/`blockers`), so we
      // must read .data, not regex-scrape the message.
      const data = (err as { data?: { code?: string; blockers?: string[] } })?.data;
      const blockers = Array.isArray(data?.blockers)
        ? data.blockers.filter(Boolean).join(", ")
        : "";
      toast({
        title:
          data?.code === "account_has_dependents"
            ? blockers
              ? `Can't delete — this account has ${blockers}. Remove those first.`
              : "Can't delete — this account has linked records (memberships, invoices, etc.)."
            : "Failed to delete account.",
        variant: "destructive",
      });
      setDeletingAccount(null);
    }
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddAccountOpen(true)}
            data-testid="accounts-add-account"
          >
            Add from Lead
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateAccountOpen(true)}
            data-testid="accounts-new-account"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New Account
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by legal name or phone…"
          className="w-[240px]"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Select
          value={kindFilter}
          onValueChange={(v) => { setKindFilter(v as "all" | ListAccountsKind); setPage(1); }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All kinds" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="member">Member</SelectItem>
            <SelectItem value="walk_in">Walk-in</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(v) => { setStatusFilter(v as "all" | ListAccountsStatus); setPage(1); }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="merged">Merged</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="hide-test-data"
            checked={hideTestData}
            onCheckedChange={(v) => { setHideTestData(v); setPage(1); }}
            data-testid="accounts-hide-test-switch"
          />
          <Label htmlFor="hide-test-data" className="text-xs cursor-pointer">
            Hide test data
            {hideTestData && hiddenTestCount > 0 && (
              <span className="ml-1 text-muted-foreground">
                ({hiddenTestCount} hidden)
              </span>
            )}
          </Label>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Legal Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>GST Treatment</TableHead>
              <TableHead>Place of Supply</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(8)].map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  {hideTestData && hiddenTestCount > 0
                    ? `No accounts match (${hiddenTestCount} test rows hidden). Toggle "Hide test data" off to reveal.`
                    : "No accounts found."}
                </TableCell>
              </TableRow>
            ) : (
              items.map((account: Account) => (
                <TableRow
                  key={account.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/accounts/${account.id}`)}
                >
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      {account.legalName}
                      {account.isInternal && (
                        <Badge variant="secondary" className="text-[10px]">In-house</Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{account.kind === "walk_in" ? "Walk-in" : "Member"}</Badge>
                  </TableCell>
                  <TableCell className="capitalize">{account.type === "company" ? "Team" : account.type}</TableCell>
                  <TableCell className="text-sm">
                    {gstTreatmentLabel(account.gstTreatment)}
                  </TableCell>
                  <TableCell>{account.placeOfSupply}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(account.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={account.status === "active" ? "default" : "secondary"}>
                      {account.status}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => navigate(`/accounts/${account.id}`)}
                        title="Edit"
                        data-testid={`accounts-row-edit-${account.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setDeletingAccount(account)}
                        title="Delete"
                        data-testid={`accounts-row-delete-${account.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && totalFiltered > 0 && (
        <ListPagination
          total={totalFiltered}
          page={safePage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          isLoading={isLoading}
          noun={["account", "accounts"]}
        />
      )}

      <AddAccountDialog open={addAccountOpen} onOpenChange={setAddAccountOpen} />
      <CreateAccountDialog open={createAccountOpen} onOpenChange={setCreateAccountOpen} />

      <AlertDialog
        open={deletingAccount !== null}
        onOpenChange={(v) => { if (!v) setDeletingAccount(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes{" "}
              <span className="font-medium">{deletingAccount?.legalName}</span>{" "}
              and its contacts. It's only allowed when the account has no
              memberships, invoices, charges, deposits, quotes, resource
              assignments, or documents — otherwise the delete is blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAccount.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteAccount.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAccount.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
